'use client';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '@benzinga/core-ui';
import type { ColumnDef } from '@benzinga/table';
import type { GovernmentTrade } from '@benzinga/gov-trades';
import { formatDate } from './formatters';
import QuoteListTable from './QuoteListTable';
import { TradeTypeBadge, govTransactionTypeTone } from './TradeTypeBadge';
import { formatTransaction } from '../GovTrades/ColumnsDef/formatTransaction';
import SeeMoreLink from '../../../app/(site)/quote/[ticker]/_components/SeeMoreLink';
import { seeMoreDestinations } from '../../../app/(site)/quote/[ticker]/_utils/seeMoreDestinations';

const partyColor = (party: string | undefined) => {
  if (!party) return 'text-gray-500';
  const normalized = party.toLowerCase();
  if (normalized.includes('democrat') || normalized === 'd') return 'text-blue-600';
  if (normalized.includes('republican') || normalized === 'r') return 'text-red-600';
  return 'text-gray-500';
};

const govTradesColumnsDef: ColumnDef<GovernmentTrade>[] = [
  {
    cellClass: 'text-gray-600',
    field: 'transaction_date',
    headerName: 'Date',
    valueFormatter: ({ data }) => formatDate(data.transaction_date),
  },
  {
    cellRenderer: ({ data }) => data.filer_info?.display_name ?? '—',
    cellClass: 'font-semibold text-gray-800',
    field: 'id',
    headerName: 'Member',
  },
  {
    cellRenderer: ({ data }) => (
      <span className={`font-semibold ${partyColor(data.filer_info?.party)}`}>{data.filer_info?.party ?? '—'}</span>
    ),
    field: 'id',
    headerName: 'Party',
  },
  {
    cellClass: 'capitalize text-gray-600',
    field: 'chamber',
    headerName: 'Chamber',
    valueFormatter: ({ data }) => data.chamber ?? '—',
  },
  {
    cellRenderer: ({ data }) => (
      <TradeTypeBadge
        label={formatTransaction(data.transaction_type ?? '')}
        tone={govTransactionTypeTone(data.transaction_type)}
      />
    ),
    field: 'transaction_type',
    headerName: 'Action',
  },
  {
    cellClass: 'text-right text-gray-700',
    field: 'amount',
    headerClass: 'text-right',
    headerName: 'Amount',
    valueFormatter: ({ data }) => data.amount || '—',
  },
];

export const GovTradesTable: React.FC<{ symbol: string; trades: GovernmentTrade[] }> = ({ symbol, trades }) => {
  const { t } = useTranslation('quote');
  const recentTrades = useMemo(
    () =>
      [...trades]
        .sort((a, b) => (b.transaction_date ?? '').localeCompare(a.transaction_date ?? ''))
        .slice(0, 20),
    [trades],
  );

  if (!recentTrades.length) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <SectionTitle level={2} size="xl" uppercase={false}>
          Congressional Trading Activity
        </SectionTitle>
        <SeeMoreLink
          action="see_more_government_trades"
          href={seeMoreDestinations.governmentTrades(symbol)}
          label={t('Quote.SeeMore.government-trades')}
        />
      </div>
      <QuoteListTable columnsDef={govTradesColumnsDef} gateType="gov-trades" rowData={recentTrades} />
    </div>
  );
};

export default GovTradesTable;
